import React, { useState, useEffect } from "react";
import { Save, LogOut, Loader2, AlertCircle, CheckCircle, RotateCcw } from "lucide-react";
import { login, logout, setToken, AdminAPIError, getTokenRole, loadTemplateContent, saveTemplateContent } from "../../api/admin";
import { defaultTcmContent } from "./demo-content";
import { getFieldLabel } from "./fieldLabels";
import FormRenderer from "../FormRenderer";
import type { TCMContent } from "./schema";

const SECTIONS = [
  { key: "siteConfig", label: "全站設定" },
  { key: "navigationContent", label: "導覽列" },
  { key: "heroContent", label: "首頁 Hero" },
  { key: "aboutContent", label: "關於醫師" },
  { key: "tcmIntroContent", label: "中醫介紹" },
  { key: "servicesContent", label: "診療項目" },
  { key: "galleryContent", label: "專業足跡" },
  { key: "faqContent", label: "常見問題" },
  { key: "healthBlogContent", label: "健康專欄" },
  { key: "contactContent", label: "聯絡與預約" },
  { key: "footerContent", label: "頁尾" },
] as const;

type SectionKey = (typeof SECTIONS)[number]["key"];

const TEMPLATE_SLUG = "tcm-v1";

const TcmAdmin: React.FC = () => {
  const [token, setTokenState] = useState<string | null>(localStorage.getItem("jkd_admin_token"));
  const [role, setRole] = useState<"admin" | "demo" | null>(getTokenRole());
  const isDemo = role === "demo";
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [editContent, setEditContent] = useState<TCMContent | null>(null);

  const [activeSection, setActiveSection] = useState<SectionKey>("siteConfig");

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    document.title = "明德中醫診所 - 後台管理";
  }, []);

  useEffect(() => {
    // Check for auto-login token in URL
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    if (urlToken) {
      setToken(urlToken);
      setTokenState(urlToken);
      setRole(getTokenRole());
      // Clean token from URL
      window.history.replaceState({}, "", `/man/${TEMPLATE_SLUG}`);
    }
  }, []);

  // Load content from API after login
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const loadContent = async () => {
      try {
        setLoading(true);
        setError(null);
        const content = await loadTemplateContent<TCMContent>(TEMPLATE_SLUG);
        setEditContent(content);
      } catch (err) {
        console.error("Failed to load template content:", err);
        setError(err instanceof Error ? err : new Error("Unknown error"));
        // Fallback to default content
        setEditContent(defaultTcmContent);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [token]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setMessage(null);
    try {
      const t = await login(password);
      setTokenState(t);
      setRole(getTokenRole());
    } catch (err) {
      setMessage({ type: "error", text: err instanceof AdminAPIError ? err.message : "登入失敗" });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editContent) return;
    setSaving(true);
    setMessage(null);
    try {
      await saveTemplateContent(TEMPLATE_SLUG, editContent);
      setMessage({ type: "success", text: "儲存成功！網站將在 1-2 分鐘後自動更新。" });
    } catch (err) {
      setMessage({ type: "error", text: err instanceof AdminAPIError ? err.message : "儲存失敗" });
      if (err instanceof AdminAPIError && err.status === 401) {
        logout();
        setTokenState(null);
        setRole(null);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!window.confirm("確定要還原為預設內容？尚未儲存的變更將會遺失。")) return;
    setEditContent(defaultTcmContent);
    setMessage(null);
  };

  const handleLogout = () => {
    logout();
    setTokenState(null);
    setRole(null);
    setEditContent(null);
    setMessage(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <h1 className="text-2xl font-bold text-brand-green">明德中醫診所</h1>
          <p className="text-gray-500 mt-1 mb-6">後台管理 — 請輸入管理密碼或 Demo 密碼</p>
          <div className="mb-6 p-3 bg-yellow-50 border border-yellow-100 rounded-lg">
            <p className="text-sm text-yellow-800">
              <span className="font-bold">Demo 密碼：</span>
              <span className="font-mono font-bold text-brand-green">demo123</span>
              <span className="block text-xs text-yellow-700 mt-1">Demo 模式僅供預覽，無法儲存變更。</span>
            </p>
          </div>
          {message && (
            <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${message.type === "error" ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}>
              {message.type === "error" ? <AlertCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
              {message.text}
            </div>
          )}
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-brand-green"
              placeholder="Password"
              autoFocus
            />
            <button
              type="submit"
              disabled={authLoading}
              className="w-full py-3 bg-brand-green text-white rounded-lg font-medium hover:bg-opacity-90 transition-colors flex items-center justify-center gap-2"
            >
              {authLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "登入"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading || !editContent) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-brand-green animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-brand-green rounded-full flex items-center justify-center text-white font-bold">
              明
            </div>
            <span className="font-bold text-brand-green">{editContent.siteConfig.siteName || "明德中醫診所"}</span>
            <span className="text-xs text-gray-400 font-mono">{TEMPLATE_SLUG}</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              className="px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50"
              title="還原為預設內容"
            >
              <RotateCcw className="w-4 h-4" />
              <span className="hidden sm:inline">重置</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving || isDemo}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                isDemo
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-brand-green text-white hover:bg-opacity-90"
              }`}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isDemo ? "Demo 模式僅供預覽" : saving ? "儲存中..." : "儲存"}
            </button>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-500 hover:text-brand-red transition-colors"
              title="登出"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Demo mode banner */}
      {isDemo && (
        <div className="bg-yellow-50 border-b border-yellow-100">
          <div className="container mx-auto px-4 py-3 flex items-center gap-2 text-yellow-800 text-sm">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span>
              你目前使用 <strong>Demo 後台</strong> 僅供預覽，所有變更不會儲存。如需完整權限請使用管理員帳號登入。
            </span>
          </div>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className="container mx-auto px-4 mt-4">
          <div className={`p-4 rounded-lg flex items-center gap-2 ${message.type === "error" ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}>
            {message.type === "error" ? <AlertCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
            {message.text}
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-6 flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <aside className="lg:w-48 shrink-0">
          <nav className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                onClick={() => setActiveSection(s.key)}
                className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors ${
                  activeSection === s.key
                    ? "bg-brand-green text-white"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                {s.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Editor */}
        <main className="flex-1 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-brand-green">
              {SECTIONS.find((s) => s.key === activeSection)?.label}
            </h2>
            <span className="text-xs text-gray-400 font-mono">{activeSection}</span>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            使用表單編輯內容，所有變更會即時更新。
          </p>
          <div className="max-h-[70vh] overflow-y-auto pr-2">
            <FormRenderer
              data={editContent[activeSection]}
              onChange={(newValue) => {
                setEditContent({
                  ...editContent,
                  [activeSection]: newValue,
                });
              }}
              path={[activeSection]}
              getLabel={getFieldLabel}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default TcmAdmin;
